"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import useCapacitor from "./useCapacitor";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import { ApiWaveDropsFeed } from "@/generated/models/ApiWaveDropsFeed";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WsMessageType, WsDropUpdateMessage } from "@/helpers/Types";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { WaveDropsSearchStrategy } from "@/contexts/wave/hooks/types";
export function useDropMessages(waveId, dropId) {
  const $ = _c(44);
  const {
    isCapacitor
  } = useCapacitor();
  const [init, setInit] = useState(false);
  let t0;
  if ($[0] !== dropId || $[1] !== waveId) {
    t0 = [QueryKey.DROPS, {
      waveId,
      limit: WAVE_DROPS_PARAMS.limit,
      dropId
    }];
    $[0] = dropId;
    $[1] = waveId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const queryKey = t0;
  let t1;
  if ($[3] !== dropId || $[4] !== waveId) {
    t1 = async t2 => {
      const {
        pageParam
      } = t2;
      const params = {
        limit: WAVE_DROPS_PARAMS.limit.toString(),
        drop_id: dropId ?? ""
      };
      if (pageParam?.serialNo) {
        params.serial_no_limit = `${pageParam.serialNo}`;
        params.search_strategy = `${pageParam.strategy}`;
      }
      const results = await commonApiFetch({
        endpoint: `waves/${waveId}/drops`,
        params
      });
      return results;
    };
    $[3] = dropId;
    $[4] = waveId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!dropId;
  const t3 = !isCapacitor;
  let t4;
  if ($[6] !== queryKey || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      initialPageParam: null,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData,
      staleTime: 60000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: t3
    };
    $[6] = queryKey;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t4);
  let t5;
  if ($[11] !== hasNextPage || $[12] !== isFetchingNextPage || $[13] !== onFetchNextPage) {
    t5 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[11] = hasNextPage;
    $[12] = isFetchingNextPage;
    $[13] = onFetchNextPage;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const fetchNextPage = t5;
  const processDrops = _temp3;
  let t6;
  if ($[15] !== data?.pages) {
    t6 = () => processDrops(data?.pages, [], false);
    $[15] = data?.pages;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const [drops, setDrops] = useState(t6);
  let t7;
  let t8;
  if ($[17] !== data) {
    t7 = () => {
      if (!data) {
        return;
      }
      setDrops(prev => processDrops(data?.pages, prev, false));
      setInit(true);
    };
    t8 = [data];
    $[17] = data;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  const lastRefetchTimeRef = useRef(0);
  const pendingRefetchRef = useRef(false);
  const timeoutRef = useRef(null);
  let t9;
  if ($[20] !== isFetching || $[21] !== isFetchingNextPage || $[22] !== refetch) {
    t9 = () => {
      const now = Date.now();
      const timeSinceLastRefetch = now - lastRefetchTimeRef.current;
      const executeRefetch = () => {
        lastRefetchTimeRef.current = Date.now();
        pendingRefetchRef.current = false;
        refetch();
      };
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (isFetching || isFetchingNextPage) {
        pendingRefetchRef.current = true;
        return;
      }
      if (timeSinceLastRefetch < 1000) {
        if (!pendingRefetchRef.current) {
          pendingRefetchRef.current = true;
          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            if (isFetching || isFetchingNextPage) {
              return;
            }
            executeRefetch();
          }, 1000 - timeSinceLastRefetch);
        }
        return;
      }
      executeRefetch();
    };
    $[20] = isFetching;
    $[21] = isFetchingNextPage;
    $[22] = refetch;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const onRefetch = t9;
  let t10;
  let t11;
  if ($[24] !== isFetching || $[25] !== isFetchingNextPage || $[26] !== refetch) {
    t10 = () => {
      if (!isFetching && !isFetchingNextPage && pendingRefetchRef.current) {
        const now_0 = Date.now();
        const timeSinceLastRefetch_0 = now_0 - lastRefetchTimeRef.current;
        if (timeSinceLastRefetch_0 >= 1000) {
          lastRefetchTimeRef.current = now_0;
          pendingRefetchRef.current = false;
          refetch();
        } else {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            if (pendingRefetchRef.current) {
              lastRefetchTimeRef.current = Date.now();
              pendingRefetchRef.current = false;
              refetch();
            }
          }, 1000 - timeSinceLastRefetch_0);
        }
      }
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    };
    t11 = [isFetching, isFetchingNextPage, refetch];
    $[24] = isFetching;
    $[25] = isFetchingNextPage;
    $[26] = refetch;
    $[27] = t10;
    $[28] = t11;
  } else {
    t10 = $[27];
    t11 = $[28];
  }
  useEffect(t10, t11);
  let t12;
  if ($[29] !== dropId || $[30] !== onRefetch || $[31] !== waveId) {
    t12 = message => {
      if (!dropId) {
        return;
      }
      if (waveId !== message.wave.id) {
        return;
      }
      onRefetch();
    };
    $[29] = dropId;
    $[30] = onRefetch;
    $[31] = waveId;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t12);
  let t13;
  if ($[33] !== fetchNextPage || $[34] !== hasNextPage) {
    t13 = async () => {
      if (hasNextPage) {
        await fetchNextPage();
      }
    };
    $[33] = fetchNextPage;
    $[34] = hasNextPage;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const manualFetch = t13;
  const t14 = isFetching ?? !init;
  let t15;
  if ($[36] !== drops || $[37] !== fetchNextPage || $[38] !== hasNextPage || $[39] !== isFetchingNextPage || $[40] !== manualFetch || $[41] !== refetch || $[42] !== t14) {
    t15 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching: t14,
      isFetchingNextPage,
      refetch,
      manualFetch
    };
    $[36] = drops;
    $[37] = fetchNextPage;
    $[38] = hasNextPage;
    $[39] = isFetchingNextPage;
    $[40] = manualFetch;
    $[41] = refetch;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
}
function _temp3(pages, previousDrops, isReverse) {
  const newDrops = pages ? mapToExtendedDrops(pages.map(_temp2), previousDrops, isReverse) : [];
  return generateUniqueKeys(newDrops, previousDrops);
}
function _temp2(page) {
  return {
    wave: page.wave,
    drops: page.drops
  };
}
function _temp(lastPage) {
  return lastPage.drops.at(-1)?.serial_no ? {
    serialNo: lastPage.drops.at(-1)?.serial_no ?? null,
    strategy: WaveDropsSearchStrategy.Older
  } : null;
}